/**
 *   This is a service for a specific store module which uses the api.ts Axios instance as a base.
 *   We separate our files for easy managment and readibility.
 *   We export this as a service to be used in other areas of the application.
 */

import { JobCardLineItem } from '@/models/JobCard.model';
import Api from '@/services/api';
import AppService from '@/services/app';
import Store from '@/store/index';


export default {
  resetTypes() {
    // reset store to empty
    Store.dispatch('jobCards/setJobTypes', {
      jobTypes: [],
    });
  },

  async listTypes(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['jobCards/jobTypes'].length < 1) {
      try {
        const response = await Api(false).get('/job_card/list_types');
        
        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetTypes();
            return response.data.message;
          } else {
            Store.dispatch('jobCards/setJobTypes', {
              jobTypes: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetTypes();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('jobCard.ts - listTypes - 47 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  resetOpen() {
    // reset store to empty
    Store.dispatch('jobCards/setOpenJobCards', {
      jobCards: [],
    });
  },

  async listOpen(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['jobCards/openJobCards'].length < 1) {
      try {
        const response = await Api(true).get('/job_card/list_open');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetOpen();
            return response.data.message;
          } else {
            Store.dispatch('jobCards/setOpenJobCards', {
              jobCards: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetOpen();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('jobCard.ts - listOpen - 90 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  resetClosed() {
    // reset store to empty
    Store.dispatch('jobCards/setClosedJobCards', {
      jobCards: [],
    });
  },

  async listClosed(refreshFromAPI: boolean) {
    // check if we need to force an API refresh or have already fetched this data and it is in the Store
    if (refreshFromAPI || Store.getters['jobCards/closedJobCards'].length < 1) {
      try {
        const response = await Api(true).get('/job_card/list_closed');

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.resetClosed();
            return response.data.message;
          } else {
            Store.dispatch('jobCards/setClosedJobCards', {
              jobCards: response.data,
            });
            return '';
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.resetClosed();
        if (error.response) {
          return error.response.statusText;
        } else {
          AppService.logSupportDebug('jobCard.ts - listClosed - 133 - ' + error);
          return Store.getters['app/messages'].couldNotConnect;
        }
      } 
    } else {
      // this data is ready to access in the Store
      return '';
    }
  },

  create(params: {
    vehicle1Id: number,
    vehicle2Id?: number,
    startDate: string,
    originalEta: string,
    jobTypeId: number,
    secondaryJobTypeId?: number,    
    odometer?: number,
    adhocMaintenanceId?: number,
    description?: string,
  }) {
    return Api(true).post('/job_card/create', params);
  },
  
  update(params: {
    id: number,
    odometer?: number,
    description?: string,
  }) {
    return Api(true).post('/job_card/update', params);
  },

  updateEta(params: {
    id: number,
    adjustedEta: string,
    adjustedEtaReason: string,
  }) {
    return Api(true).post('/job_card/update_eta', params);
  },

  close(params: {
    id: number,
  }) {
    return Api(true).post('/job_card/close', params);
  },

  approve(params: {
    id: number,
  }) {
    return Api(true).post('/job_card/approve', params);
  },
  
  delete(params: {
    id: number,
  }) {
    return Api(true).post('/job_card/delete', params);
  },

  search(config: {params: { 
    jobTypeIds?: string,
    vehicleId?: number,
    startDate?: string,
    endDate?: string,
  }}) {
    return Api(true).get('/job_card/search', config);
  },

  printPdf(params: {
    id: number,
  }) {
    return Api(true).post('/job_card/print', params);
  },

  listLineItemsByJobCard(config: {params: { jobCardId: number }}) {
    return Api(true).get('/job_card_line_item/list_by_job_card', config);
  },

  createLineItems(params: {
    jobCardId: number,
    lineItems: JobCardLineItem[],
  }) {
    return Api(true).post('/job_card_line_item/create', params);
  },

  getJobCardVehicleOdometers(config: {params: { jobCardId: number }}) {
    return Api(true).get('/job_card/vehicle_odometers', config);
  },
};
